const CONTINENTS = ['asia', 'europe', 'americas', 'oceania', 'africa'];
const CATEGORIES = ['road', 'gran_fondo', 'gravel', 'endurance', 'mtb', 'para', 'other', 'track', 'festival'];

export function safeHttps(value) {
  try { const u = new URL(value); return u.protocol === 'https:' && !!u.hostname && !u.username && !u.password; }
  catch { return false; }
}

export function normalizeState(params, countries) {
  const year = /^20\d{2}$/.test(params.get('year') || '') ? params.get('year') : '2027';
  const continent = CONTINENTS.includes(params.get('continent')) ? params.get('continent') : '';
  const candidate = countries.find(c => c.iso2 === (params.get('country') || '').toUpperCase());
  const country = candidate && (!continent || candidate.continent === continent) ? candidate.iso2 : '';
  return {year, continent: continent || (country ? candidate.continent : ''), country,
    audience: ['join', 'watch'].includes(params.get('audience')) ? params.get('audience') : 'all',
    category: CATEGORIES.includes(params.get('category')) ? params.get('category') : 'all',
    q: (params.get('q') || '').trim().slice(0,200)};
}

const searchable = value => String(value || '').normalize('NFKD').replace(/\p{M}/gu,'').toLowerCase();

export function filterEvents(events, state) {
  return events.filter(e => (!state.country || e.country === state.country) && String(e.year) === state.year &&
    (state.audience === 'all' || e.audience === state.audience || e.audience === 'both') &&
    (state.category === 'all' || e.category === state.category) &&
    (!state.q || searchable(e.searchText || e.id).includes(searchable(state.q))));
}

export function sortEvents(events) {
  return [...events].sort((a, b) => (a.start || '9999').localeCompare(b.start || '9999') ||
    Number(a.order || 0) - Number(b.order || 0) || a.id.localeCompare(b.id));
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('arvento-race-atlas')) {
  class RaceAtlas extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.countryList = this.querySelector('.arv-ra__countries');
      this.addThemeCountries();
      this.collectCountries();
      this.state = normalizeState(new URLSearchParams(location.search), this.countries);
      this.list = this.querySelector('[data-ra-event-list]');
      this.form = this.querySelector('[data-ra-filters]');
      this.addThemeEvents();
      this.collectEvents();
      this.classList.add('is-ready');
      this.addEventListener('click', e => {
        const continent = e.target.closest('[data-ra-continent]');
        const country = e.target.closest('[data-ra-country]');
        const reset = e.target.closest('[data-ra-reset]');
        if (continent) {
          e.preventDefault(); this.state.continent = continent.dataset.raContinent; this.state.country = '';
          this.render(true); this.move('[data-ra-countries-title]');
        } else if (country) {
          e.preventDefault(); this.state.country = country.dataset.raCountry; this.state.continent = country.dataset.continent;
          this.render(true); this.move('[data-ra-events-title]');
        } else if (reset) {
          e.preventDefault(); Object.assign(this.state,{year:'2027',audience:'all',category:'all',q:''}); this.render(true);
        }
      }, {signal});
      this.addEventListener('keydown', e => {
        if (e.key === ' ' && e.target.matches('[data-ra-continent],[data-ra-country]')) { e.preventDefault(); e.target.click(); }
      }, {signal});
      this.form.addEventListener('change', () => {
        for (const key of ['year','audience','category']) this.state[key] = this.form.elements[key].value;
        this.render(true);
      }, {signal});
      this.form.addEventListener('submit',e => e.preventDefault(),{signal});
      this.form.addEventListener('input',e => {
        if (e.target.name === 'q') { this.state.q = e.target.value.trim().slice(0,200); this.render(true); }
      },{signal});
      window.addEventListener('popstate', () => { this.state = normalizeState(new URLSearchParams(location.search),this.countries); this.render(); }, {signal});
      this.localizeDates();
      this.render();
      this.loadRemaining(signal);
    }

    disconnectedCallback() { this.controller?.abort(); this.controller = null; }

    collectCountries() {
      this.countries = [...this.countryList.querySelectorAll('[data-ra-country]')].map(node => ({node, iso2:node.dataset.raCountry, continent:node.dataset.continent}));
    }

    themeData() {
      if (this.themePayload) return this.themePayload;
      const data = this.querySelector('[data-ra-theme-events]');
      if (!data) return null;
      try { this.themePayload = JSON.parse(data.textContent); }
      catch { this.loadFailed = true; this.querySelector('[data-ra-load-error]').hidden = false; return null; }
      const requested = (this.getAttribute('lang') || 'en').toLowerCase();
      this.themeLocale = requested.startsWith('zh') ? 'zh-TW' : ['ja','it','es'].find(l => requested === l || requested.startsWith(l+'-')) || 'en';
      this.dataset.version = this.themePayload.version;
      return this.themePayload;
    }

    addThemeCountries() {
      const payload = this.themeData();
      const template = this.querySelector('[data-ra-theme-country-template]');
      if (!payload || !template) return;
      for (const country of payload.countries || []) {
        if (this.countryList.querySelector(`[data-ra-country="${country.iso2}"]`)) continue;
        const card = template.content.querySelector('a').cloneNode(true);
        const name = country.names[this.themeLocale];
        Object.assign(card.dataset,{raCountry:country.iso2,continent:country.continent,name});
        card.querySelector('.arv-ra__country-name').textContent = name;
        card.querySelector('.arv-ra__country-code').textContent = '🇨🇳 '+country.iso2+' ↗';
        const next = [...this.countryList.children].find(n => n.dataset.continent !== country.continent);
        this.countryList.insertBefore(card,next || null);
      }
    }

    addThemeEvents() {
      const payload = this.themeData();
      const template = this.querySelector('[data-ra-theme-template]');
      if (!payload || !template) return;
      const locale = this.themeLocale;
      for (const [value,label] of Object.entries(payload.ui[locale].categories || {})) {
        if (![...this.form.elements.category.options].some(o => o.value === value)) this.form.elements.category.append(new Option(label,value));
      }
      if (!this.form.elements.q) {
        const label = document.createElement('label');
        label.textContent = payload.ui[locale].search;
        const input = document.createElement('input');
        input.type = 'search'; input.name = 'q'; input.maxLength = 200; input.autocomplete = 'off';
        label.append(input); this.form.insertBefore(label,this.form.querySelector('[data-ra-reset]'));
      }
      const known = new Set([...this.list.querySelectorAll('[data-ra-event]')].map(n => n.dataset.raEvent));
      const optionLabel = (name,value) => [...this.form.elements[name].options].find(o => o.value === value)?.textContent || '';
      for (const event of payload.events) {
        const country = this.countries.find(c => c.iso2 === event.country);
        if (known.has(event.id) || !country || !safeHttps(event.official_url) || !safeHttps(event.source_url)) continue;
        const card = template.content.querySelector('li').cloneNode(true);
        Object.assign(card.dataset,{raEvent:event.id,country:event.country,year:String(event.year),category:event.category,audience:event.audience,start:event.date_status === 'tba' ? '' : event.start_date || '',order:String(event.sort_order || 0)});
        const text = (selector,value) => { card.querySelector(selector).textContent = value; };
        text('h3',event.translations.name?.[locale] || event.name);
        text('[data-ra-theme-location]',country.node.dataset.name+' · '+event.translations.city[locale]);
        text('[data-ra-theme-type]',optionLabel('category',event.category)+' · '+optionLabel('audience',event.audience));
        text('[data-ra-theme-summary]',event.translations.summary[locale]);
        const status = card.querySelector('[data-ra-theme-status]');
        status.textContent = payload.ui[locale].dateStatuses?.[event.date_status] || status.getAttribute('data-'+event.date_status);
        const registration = card.querySelector('[data-ra-theme-registration]');
        registration.textContent = registration.getAttribute('data-'+event.registration_status.replaceAll('_','-'));
        const date = card.querySelector('[data-ra-theme-date]');
        if (event.date_status !== 'tba' && event.start_date) {
          for (const iso of [event.start_date,...(event.end_date && event.end_date !== event.start_date ? [event.end_date] : [])]) {
            if (date.childNodes.length) date.append(document.createTextNode(' – '));
            const time = document.createElement('time'); time.dateTime = iso; time.textContent = iso; date.append(time);
          }
        } else date.textContent = status.getAttribute('data-tba');
        card.querySelector('[data-ra-official]').href = event.official_url;
        card.querySelector('.arv-ra__source').href = event.source_url;
        const verified = card.querySelector('[data-ra-theme-verified]'); verified.dateTime = event.verified_on; verified.textContent = event.verified_on;
        this.list.append(card); known.add(event.id);
      }
    }

    collectEvents() {
      const unique = new Map();
      for (const node of this.list.querySelectorAll('[data-ra-event]')) {
        const previous = unique.get(node.dataset.raEvent);
        if (!previous) unique.set(node.dataset.raEvent,node);
        else if (previous.dataset.raSource === 'theme' && node.dataset.raSource !== 'theme') { previous.remove(); unique.set(node.dataset.raEvent,node); }
        else node.remove();
      }
      this.events = [...unique.values()].map(node => ({node, id:node.dataset.raEvent,
        country:node.dataset.country, year:node.dataset.year, category:node.dataset.category,
        audience:node.dataset.audience, start:node.dataset.start, order:node.dataset.order, searchText:node.textContent}));
      for (const event of sortEvents(this.events)) this.list.append(event.node);
      const years = new Set(['2027','2028',this.state.year,...this.events.map(e => e.year)]);
      const select = this.form.elements.year;
      select.replaceChildren(...[...years].sort().map(year => new Option(year,year)));
      for (const link of this.querySelectorAll('[data-ra-official]')) {
        if (!safeHttps(link.getAttribute('href'))) link.remove();
      }
    }

    localizeDates() {
      const locale = this.getAttribute('lang') || 'en';
      for (const node of this.querySelectorAll('time[datetime]')) {
        const iso = node.dateTime;
        if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) node.textContent = new Intl.DateTimeFormat(locale,{year:'numeric',month:'short',day:'numeric',timeZone:'UTC'}).format(new Date(iso+'T12:00:00Z'));
      }
    }

    move(selector) {
      const title = this.querySelector(selector);
      title.focus({preventScroll:true});
      title.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',block:'start'});
    }

    render(save = false) {
      const s = this.state;
      for (const n of this.querySelectorAll('[data-ra-continent]')) n.setAttribute('aria-pressed',String(n.dataset.raContinent === s.continent));
      for (const c of this.countries) {
        c.node.hidden = !!s.continent && c.continent !== s.continent;
        c.node.setAttribute('aria-pressed',String(c.iso2 === s.country));
      }
      const title = this.querySelector('[data-ra-country-name]');
      title.textContent = this.countries.find(c => c.iso2 === s.country)?.node.dataset.name || '';
      for (const key of ['year','audience','category']) this.form.elements[key].value = s[key];
      if (this.form.elements.q && this.form.elements.q.value.trim() !== s.q) this.form.elements.q.value = s.q;
      this.querySelector('[data-ra-events]').hidden = !s.country;
      const matches = new Set(filterEvents(this.events,s));
      for (const event of this.events) event.node.hidden = !matches.has(event);
      this.querySelector('[data-ra-count]').textContent = String(matches.size);
      const empty = this.querySelector('[data-ra-empty]');
      const countryEvents = this.events.some(e => e.country === s.country && e.year === s.year);
      empty.hidden = matches.size > 0 || !!this.loading || !!this.loadFailed;
      empty.textContent = (countryEvents || s.q ? this.dataset.noResults : this.dataset.noEvents).replace('__YEAR__',s.year);
      const label = this.querySelector('[data-ra-continent-name]');
      label.textContent = this.querySelector(`button[data-ra-continent="${s.continent}"]`)?.textContent || this.dataset.allContinents;
      if (save) {
        const url = new URL(location.href);
        for (const key of ['year','continent','country','audience','category','q']) {
          if (s[key] && s[key] !== 'all') url.searchParams.set(key,s[key]); else url.searchParams.delete(key);
        }
        history.replaceState(null,'',url);
      }
    }

    async loadRemaining(signal) {
      const nav = this.querySelector('[data-ra-pagination]');
      if (!nav) return;
      const pageLinks = root => [...root.querySelectorAll('[data-ra-next],[data-ra-previous]')].map(a => a.getAttribute('href'));
      const queue = pageLinks(nav);
      this.loading = queue.length > 0;
      const status = this.querySelector('[data-ra-loading]');
      status.hidden = !this.loading;
      this.render();
      const visited = new Set();
      try {
        while (queue.length) {
          const url = new URL(queue.shift(),location.href);
          if (url.origin !== location.origin) throw new Error('pagination');
          url.searchParams.set('section_id',this.dataset.sectionId);
          // Preserve unpublished preview and alternate template while fetching later pages.
          for (const key of ['preview_theme_id','view']) {
            const value = new URL(location.href).searchParams.get(key);
            if (value) url.searchParams.set(key,value);
          }
          if (visited.has(url.href)) continue;
          visited.add(url.href);
          const response = await fetch(url,{signal,credentials:'same-origin'});
          if (!response.ok) throw new Error('page');
          const html = new DOMParser().parseFromString(await response.text(),'text/html');
          const root = html.querySelector('arvento-race-atlas');
          if (!root) throw new Error('section');
          const knownCountries = new Set(this.countries.map(c => c.iso2));
          for (const card of root.querySelectorAll('[data-ra-country]')) if (!knownCountries.has(card.dataset.raCountry)) this.countryList.append(card);
          this.collectCountries();
          this.state = normalizeState(new URLSearchParams(location.search),this.countries);
          const known = new Map(this.events.map(e => [e.id,e.node]));
          for (const card of root.querySelectorAll('[data-ra-event]')) {
            const previous = known.get(card.dataset.raEvent);
            if (!previous) { this.list.append(card); known.set(card.dataset.raEvent,card); }
            else if (previous.dataset.raSource === 'theme' && card.dataset.raSource !== 'theme') { previous.replaceWith(card); known.set(card.dataset.raEvent,card); }
          }
          queue.push(...pageLinks(root));
          this.collectEvents(); this.localizeDates(); this.render();
        }
        nav.hidden = true;
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.loadFailed = true;
        this.querySelector('[data-ra-load-error]').hidden = false;
        // Keep server pagination links usable on a failed incremental request.
      } finally {
        if (!signal.aborted) { this.loading = false; status.hidden = true; this.render(); }
      }
    }
  }
  customElements.define('arvento-race-atlas', RaceAtlas);
}
